import {
  type DeliveryDateVariant,
  FILTER_NO_VALUE,
} from '../../../contracts/schemas/order.js';
import type { DbClient, OrderRow } from './types.js';

export interface DateRange {
  from?: string;
  to?: string;
}

const DELIVERY_DATE_COLUMNS: Record<DeliveryDateVariant, string> = {
  agreed: 'orders.agreed_delivery_date',
  handover: 'orders.handover_date',
  actual: 'orders.actual_delivery_date',
};

/** Every filter the orders list understands, shared by `GET /orders`, the
 *  export id resolution and (minus the order-only parts) `GET /order-lines`. */
export interface OrdersFilterParams {
  companyId: string;
  sort?: string;
  search?: string;
  types?: string[];
  statuses?: string[];
  tracingStatuses?: string[];
  suppliers?: string[];
  buyers?: string[];
  vendors?: string[];
  receivers?: string[];
  supplierCountries?: string[];
  tags?: string[];
  seasons?: string[];
  brandIds?: string[];
  styleCustomPropertyIds?: string[];
  styleCollectionIds?: string[];
  styleTypeIds?: string[];
  responsibleUserIds?: string[];
  orderNumber?: string;
  orderNumbers?: string[];
  isArchived?: boolean;
  updatedAfter?: Date;
  updatedBefore?: Date;
  createdAfter?: Date;
  createdBefore?: Date;
  orderDateFrom?: string;
  orderDateTo?: string;
  deliveryDates?: Partial<Record<DeliveryDateVariant, DateRange>>;
  scope?: {
    seesAll: boolean;
    deniedIds: string[];
    allowedIds: string[];
  };
}

export interface ListOrdersDbParams extends OrdersFilterParams {
  page: number;
  count: number;
}

/**
 * How line-derived filters (style attributes, tracing status, the line half of
 * the search) reach the lines of the row being listed. `matchLine` wraps a
 * predicate over `ol` (order_lines) and `s` (styles).
 */
export interface LineFilterScope {
  matchLine: (predicate: string) => string;
  tracingStatus: string;
  lineSearchColumns: string[];
}

/** Status of the Tracing request a line was sent to (reflection pointer, the
 *  side's own id) or references (`tracing_orders.id`, read from the hub order's
 *  side; internal orders read the purchase side). */
export function lineTracingStatusSql(line: string): string {
  const side = (table: string, referencedSide: string) =>
    `(SELECT t.status FROM ${table} t
       WHERE t.is_removed = 0
         AND (t.id = ${line}.tracing_reflection_order_id
              OR (${referencedSide} AND t.tracing_order_id = ${line}.referenced_tracing_order_id))
       LIMIT 1)`;
  return `COALESCE(${side('tracing_purchase_orders', "orders.order_type <> 'SALES_ORDER'")}, ${side('tracing_sales_orders', "orders.order_type = 'SALES_ORDER'")})`;
}

// All requests sent from one hub order share one status, so any line's will do.
export const ORDER_TRACING_STATUS_SQL = `(SELECT MIN(${lineTracingStatusSql('ol')})
   FROM order_lines ol
  WHERE ol.order_id = orders.id
    AND (ol.tracing_reflection_order_id IS NOT NULL OR ol.referenced_tracing_order_id IS NOT NULL))`;

export const ORDER_LINES_SCOPE: LineFilterScope = {
  matchLine: predicate =>
    `EXISTS (SELECT 1 FROM order_lines ol JOIN styles s ON s.id = ol.style_id WHERE ol.order_id = orders.id AND (${predicate}))`,
  tracingStatus: ORDER_TRACING_STATUS_SQL,
  lineSearchColumns: [],
};

const companySubquery = (column: string, select: string) =>
  `(SELECT ${select} FROM companies_view cv WHERE cv.id = ${column})`;

export const companyNameSql = (column: string) =>
  companySubquery(column, 'LOWER(cv.display_name)');

export const SUPPLIER_COUNTRY_CODE_SQL = companySubquery(
  'orders.supplier_company_id',
  'cv.country',
);

export const SUPPLIER_COUNTRY_NAME_SQL = companySubquery(
  'orders.supplier_company_id',
  'cv.country_name',
);

/** Lifecycle rank; anything outside the lifecycle sorts after it, empty last.
 *  A simple CASE evaluates `expr` once, which matters when it is a subquery. */
export const lifecycleRankSql = (expr: string) =>
  `CASE COALESCE(${expr}, '') WHEN '' THEN NULL WHEN 'IN_PROGRESS' THEN 1 WHEN 'IN_REVIEW' THEN 2 WHEN 'COMPLETED' THEN 3 ELSE 4 END`;

const lowerText = (column: string) => `NULLIF(LOWER(${column}), '')`;

export interface SortColumn {
  expr: string;
  // created_at / updated_at are always set; leaving NULLS alone keeps the
  // (for_company_id, updated_at DESC) index usable for the default sort.
  nullable: boolean;
}

export const sortable = (expr: string, nullable = true): SortColumn => ({
  expr,
  nullable,
});

export const ORDER_SORT_COLUMNS: Record<string, SortColumn> = {
  updatedAt: sortable('orders.updated_at', false),
  createdAt: sortable('orders.created_at', false),
  orderNumber: sortable('LOWER(orders.order_number)', false),
  orderType: sortable('orders.order_type'),
  category: sortable('orders.order_sub_type'),
  status: sortable(lifecycleRankSql('orders.status')),
  tracingStatus: sortable(lifecycleRankSql(ORDER_TRACING_STATUS_SQL)),
  supplier: sortable(companyNameSql('orders.supplier_company_id')),
  buyer: sortable(companyNameSql('orders.buyer_company_id')),
  receiver: sortable(companyNameSql('orders.receiver_company_id')),
  vendor: sortable(companyNameSql('orders.vendor_company_id')),
  factory: sortable(companyNameSql('orders.factory')),
  supplierCountry: sortable(SUPPLIER_COUNTRY_NAME_SQL),
  lines: sortable(
    '(SELECT COUNT(*) FROM order_lines ol WHERE ol.order_id = orders.id)',
    false,
  ),
  orderDate: sortable('orders.order_date'),
  agreedDeliveryDate: sortable('orders.agreed_delivery_date'),
  handoverDate: sortable('orders.handover_date'),
  actualDeliveryDate: sortable('orders.actual_delivery_date'),
  expectedShippingDate: sortable('orders.expected_shipping_date'),
  shippingDate: sortable('orders.shipping_date'),
  shipmentReference: sortable(lowerText('orders.shipment_reference')),
};

const DEFAULT_ORDER_BY = 'orders.updated_at DESC, orders.id DESC';

/** Parses a `field:DIR` sort param against a whitelist. */
export function parseSort(
  sort: string | undefined,
  columns: Record<string, SortColumn>,
): { column: SortColumn; direction: 'ASC' | 'DESC' } | undefined {
  if (!sort) return undefined;
  const [field, dir] = sort.split(':');
  const column = Object.hasOwn(columns, field) ? columns[field] : undefined;
  if (!column) return undefined;
  return { column, direction: dir === 'ASC' ? 'ASC' : 'DESC' };
}

export function sortClause(column: SortColumn, direction: string): string {
  return `${column.expr} ${direction}${column.nullable ? ' NULLS LAST' : ''}`;
}

/** Resolves the `field:DIR` sort param to a safe ORDER BY clause. */
export function resolveOrderBy(sort: string | undefined): string {
  const parsed = parseSort(sort, ORDER_SORT_COLUMNS);
  if (!parsed) return DEFAULT_ORDER_BY;
  return `${sortClause(parsed.column, parsed.direction)}, orders.id ${parsed.direction}`;
}

export function escapeLike(value: string): string {
  return value.replace(/[\\%_]/g, c => `\\${c}`);
}

function splitNoValue(values: string[] | undefined): {
  values: string[];
  noValue: boolean;
} {
  const list = values ?? [];
  return {
    values: list.filter(value => value !== FILTER_NO_VALUE),
    noValue: list.includes(FILTER_NO_VALUE),
  };
}

/** WHERE conditions + bind params for a filter set, so the paginated list and the
 *  export resolve to the same clause by construction. */
export function buildOrdersWhere(
  params: OrdersFilterParams,
  lines: LineFilterScope = ORDER_LINES_SCOPE,
): { conditions: string[]; binds: Record<string, unknown> } {
  const conditions: string[] = ['orders.for_company_id = :companyId'];
  const binds: Record<string, unknown> = { companyId: params.companyId };

  const inList = (
    field: string,
    values: string[],
    bindingName: string,
    operator: 'IN' | 'NOT IN' = 'IN',
  ) => {
    const placeholders = values.map((value, i) => {
      binds[`${bindingName}${i}`] = value;
      return `:${bindingName}${i}`;
    });
    return `${field} ${operator} (${placeholders.join(', ')})`;
  };

  /** OR within a filter: the listed values, plus the empty case for `N/A`. */
  const addMultiValue = (
    raw: string[] | undefined,
    matchValues: (values: string[]) => string,
    matchEmpty?: string,
  ) => {
    const { values, noValue } = splitNoValue(raw);
    const parts: string[] = [];
    if (values.length > 0) parts.push(matchValues(values));
    if (noValue && matchEmpty !== undefined) parts.push(matchEmpty);
    if (parts.length > 0) conditions.push(parts.join(' OR '));
  };

  const addIdFilter = (
    column: string,
    raw: string[] | undefined,
    bindingName: string,
    nullable = true,
  ) =>
    addMultiValue(
      raw,
      values => inList(column, values, bindingName),
      nullable ? `${column} IS NULL` : undefined,
    );

  addIdFilter('orders.order_type', params.types, 'orderType', false);
  addIdFilter('orders.status', params.statuses, 'status');
  addIdFilter('orders.supplier_company_id', params.suppliers, 'supplier');
  addIdFilter('orders.buyer_company_id', params.buyers, 'buyer');
  addIdFilter('orders.vendor_company_id', params.vendors, 'vendor');
  addIdFilter('orders.receiver_company_id', params.receivers, 'receiver');

  addMultiValue(
    params.tracingStatuses,
    values => inList(lines.tracingStatus, values, 'tracingStatus'),
    `${lines.tracingStatus} IS NULL`,
  );

  addMultiValue(
    params.supplierCountries,
    values =>
      `EXISTS (SELECT 1 FROM companies_view cv WHERE cv.id = orders.supplier_company_id AND ${inList('cv.country', values, 'supplierCountry')})`,
    `NOT EXISTS (SELECT 1 FROM companies_view cv WHERE cv.id = orders.supplier_company_id AND COALESCE(cv.country, '') <> '')`,
  );

  addMultiValue(
    params.tags,
    values =>
      values
        .map((tag, i) => {
          binds[`tagLike${i}`] = `%,${escapeLike(tag.toLowerCase())},%`;
          return `LOWER(','||orders.tags||',') LIKE :tagLike${i} ESCAPE '\\'`;
        })
        .join(' OR '),
    "COALESCE(orders.tags, '') = ''",
  );

  addMultiValue(
    params.responsibleUserIds,
    values =>
      `EXISTS (SELECT 1 FROM order_responsible_users oru WHERE oru.order_id = orders.id AND ${inList('oru.user_id', values, 'responsibleUser')})`,
    'NOT EXISTS (SELECT 1 FROM order_responsible_users oru WHERE oru.order_id = orders.id)',
  );

  const addStyleListFilter = (
    column: 'seasons' | 'brands' | 'custom_properties' | 'collections',
    raw: string[] | undefined,
    bindPrefix: string,
  ) =>
    addMultiValue(
      raw,
      values =>
        // styles.<column> holds comma-joined style_properties ids
        lines.matchLine(
          values
            .map((propertyId, i) => {
              binds[`${bindPrefix}${i}`] = `%,${escapeLike(propertyId)},%`;
              return `','||s.${column}||',' LIKE :${bindPrefix}${i} ESCAPE '\\'`;
            })
            .join(' OR '),
        ),
      `NOT ${lines.matchLine(`COALESCE(s.${column}, '') <> ''`)}`,
    );

  addStyleListFilter('seasons', params.seasons, 'seasonLike');
  addStyleListFilter('brands', params.brandIds, 'brandLike');
  addStyleListFilter(
    'custom_properties',
    params.styleCustomPropertyIds,
    'customPropertyLike',
  );
  addStyleListFilter(
    'collections',
    params.styleCollectionIds,
    'collectionLike',
  );

  addMultiValue(params.styleTypeIds, values =>
    lines.matchLine(inList('s.style_type_id', values, 'styleType')),
  );

  if (params.search !== undefined && params.search !== '') {
    binds.searchLike = `%${escapeLike(params.search.toLowerCase())}%`;
    const like = (expr: string) =>
      `LOWER(${expr}) LIKE :searchLike ESCAPE '\\'`;
    const lineMatches = [
      like('s.name'),
      `EXISTS (SELECT 1 FROM style_codes sc WHERE sc.style_id = ol.style_id AND ${like('sc.code_value')})`,
      ...lines.lineSearchColumns.map(like),
    ];
    conditions.push(
      [
        like('orders.order_number'),
        like('orders.shipment_reference'),
        `EXISTS (SELECT 1 FROM companies_view cv WHERE cv.id IN (orders.supplier_company_id, orders.buyer_company_id) AND ${like('cv.display_name')})`,
        lines.matchLine(lineMatches.join(' OR ')),
      ].join(' OR '),
    );
  }

  if (params.orderNumber !== undefined) {
    conditions.push('orders.order_number = :orderNumber');
    binds.orderNumber = params.orderNumber;
  }
  if (params.orderNumbers !== undefined && params.orderNumbers.length > 0) {
    conditions.push(
      inList(
        'LOWER(orders.order_number)',
        params.orderNumbers.map(value => value.toLowerCase()),
        'orderNumbers',
      ),
    );
  }
  if (params.isArchived !== undefined) {
    conditions.push('orders.is_archived = :isArchived');
    binds.isArchived = params.isArchived;
  }

  const addRange = (
    column: string,
    from: unknown,
    to: unknown,
    bindPrefix: string,
  ) => {
    if (from !== undefined) {
      conditions.push(`${column} >= :${bindPrefix}From`);
      binds[`${bindPrefix}From`] = from;
    }
    if (to !== undefined) {
      conditions.push(`${column} <= :${bindPrefix}To`);
      binds[`${bindPrefix}To`] = to;
    }
  };

  addRange(
    'orders.updated_at',
    params.updatedAfter,
    params.updatedBefore,
    'updated',
  );
  addRange(
    'orders.created_at',
    params.createdAfter,
    params.createdBefore,
    'created',
  );
  addRange(
    'orders.order_date',
    params.orderDateFrom,
    params.orderDateTo,
    'orderDate',
  );
  for (const [variant, range] of Object.entries(params.deliveryDates ?? {})) {
    addRange(
      DELIVERY_DATE_COLUMNS[variant as DeliveryDateVariant],
      range?.from,
      range?.to,
      `${variant}DeliveryDate`,
    );
  }

  // IAM per-record scope: seesAll → exclude denied records; otherwise → keep
  // only explicitly shared records (empty share list ⇒ no rows).
  if (params.scope !== undefined) {
    const { seesAll, deniedIds, allowedIds } = params.scope;
    if (seesAll) {
      if (deniedIds.length > 0) {
        conditions.push(
          inList('orders.id', deniedIds, 'deniedOrderId', 'NOT IN'),
        );
      }
    } else if (allowedIds.length === 0) {
      conditions.push('1 = 0');
    } else {
      conditions.push(inList('orders.id', allowedIds, 'allowedOrderId'));
    }
  }

  return { conditions, binds };
}

export async function listOrdersDb(
  db: DbClient,
  params: ListOrdersDbParams,
): Promise<{ rows: OrderRow[]; totalCount: number }> {
  const { conditions, binds } = buildOrdersWhere(params);

  const { rows, count: totalCount } = await db.select<OrderRow>(
    {
      tableName: 'orders',
      fields: `orders.*,
         (SELECT COUNT(*) FROM order_lines ol WHERE ol.order_id = orders.id) AS line_count,
         ${ORDER_TRACING_STATUS_SQL} AS tracing_request_status,
         ${SUPPLIER_COUNTRY_CODE_SQL} AS supplier_country_code,
         ${SUPPLIER_COUNTRY_NAME_SQL} AS supplier_country_name`,
      where: { conditions },
      orderBy: resolveOrderBy(params.sort),
      offset: params.page * params.count,
      limit: params.count,
      count: { field: '*', alias: 'count' },
    },
    binds,
  );

  return { rows, totalCount };
}
